import React, { useContext, useMemo } from "react";
import Title from "../../../components/Title";
import BarGraph from "../../../components/BarGraph";
import { Detalle } from "../../../utils/constants";
import AppContext, { IFilters } from "../../../AppContext";
import { gql, useQuery } from "@apollo/client";
import {
  getStepsFixed as getSteps,
  getMinFixed as getMin,
  getMaxFixed as getMax,
} from "../../../utils/functions";
import { MessageBar, Spinner, SpinnerSize } from "@fluentui/react";
import { useTranslation } from "react-i18next";

export const titleEn = "Occasion position";
export const title = "Tendencia por momento del día";
export const filters: IFilters = {
  limitedPeriodo: false,
  showMedidas: true,
  hasTicketMedio: false,
  isDesglose: true,
  showEntorno: false,
  showSegmentos: true,
  showMyData: false,
};


const norm = (s: string) =>
  (s || "")
    .replace(/_/g, " ")
    .replace(/[\u2010-\u2015]/g, "-") 
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, ""); 


const getOccasionTranslated = (label: string, t: (k: string) => string): string | null => {
  
  const baseOccasionKeys: Record<string, string> = {
    "mañanas": "Mañanas",
    "tardes": "Tardes",
    "comidas (l-v)": "Comidas (L-V)",
    "comidas (s-d)": "Comidas (S-D)",
    "cenas (l-j)": "Cenas (L-J)",
    "cenas (v-d)": "Cenas (V-D)",
  };

  const lk = norm(label);
 
  const translated =
    lk === norm("mañanas") ? t(baseOccasionKeys["mañanas"]) :
    lk === norm("tardes") ? t(baseOccasionKeys["tardes"]) :
    lk === norm("comidas (l-v)") ? t(baseOccasionKeys["comidas (l-v)"]) :
    lk === norm("comidas (s-d)") ? t(baseOccasionKeys["comidas (s-d)"]) :
    lk === norm("cenas (l-j)") ? t(baseOccasionKeys["cenas (l-j)"]) :
    lk === norm("cenas (v-d)") ? t(baseOccasionKeys["cenas (v-d)"]) :
    null;

  return translated;
};

function translateExportKeys(input: any, t: (k: string) => string): any {
  const translateKey = (raw: string): string => {
    if (!raw || typeof raw !== "string") return raw as any;

    let s = raw.replace(/_/g, " ").trim();
    const low = s.toLowerCase();
    const lowNorm = norm(s);

   
    const occ = getOccasionTranslated(s, t);
    if (occ) return occ;

    if (lowNorm === norm("momento")) return t("Moment") as string;
    if (lowNorm === norm("mercado")) return t("Market") as string;

    if (lowNorm === norm("ventas")) return t("Sales") as string;
    if (lowNorm === norm("tickets")) return t("Tickets") as string;
    if (lowNorm === norm("ticket medio") || lowNorm === norm("ticketmedio"))
      return t("Average-ticket") as string;
    if (lowNorm === norm("totales")) return t("Totals") as string;
    if (lowNorm === norm("total")) return t("Total") as string;
    if (lowNorm === norm("comparables") || lowNorm === norm("comparable"))
      return t("Comparables") as string;

    
    const direct = t(s) as string;
    return direct !== s ? direct : s;
  };

  if (Array.isArray(input)) return input.map((el) => translateExportKeys(el, t));
  if (input && typeof input === "object") {
    const out: any = {};
    Object.keys(input).forEach((k) => {
      const nk = translateKey(k);
      out[nk] = translateExportKeys(input[k], t);
    });
    return out;
  }
  return input;
}

const translateOccasionLabel = (label: string, t: (k: string) => string) => {
  const occ = getOccasionTranslated(label, t);
  if (occ) return occ;

  const s = (label || "").trim();
  const lowNorm = norm(s);

  if (lowNorm === norm("total")) return t("Total");
  const direct = t(s);
  return direct !== s ? direct : s;
};

const Graph = () => {
  const { t } = useTranslation();
  const { semanas, filters, user } = useContext(AppContext);
  const isDesglosado = filters.detalle === Detalle.Desglose;

  const GET_GRAPH = gql`
    query (
      $yr1: Int!
      $wk1: Int!
      $yr2: Int!
      $wk2: Int!
      $var: Int
      $segment: String
      $segmentType: Int
    ) {
      graph(
        Graph: "Tendencias por momento del dia"
        WeekRange: {
          Start: { Year: $yr1, Week: $wk1 }
          End: { Year: $yr2, Week: $wk2 }
        }
        Filters: {
          Variables: $var
          Segmento: $segment
          SegmentType: $segmentType
        }
      )
    }
  `;

  const qry = useQuery(GET_GRAPH, {
    fetchPolicy: "no-cache",
    variables: {
      yr1: semanas[0].year,
      wk1: semanas[0].week,
      yr2: semanas[1].year,
      wk2: semanas[1].week,
      var: 0, 
    },
  });

  let errorMsg = t("An-error-has-occur-when-recovering-data");
  let loading = qry.loading || qry.data === undefined;
  let error = !semanas || !!qry.error;

  let labels: string[] = [],
    values_total: number[][] = useMemo(() => [], []),
    values_desglose: number[][] = useMemo(() => [], []);
  let data: any;

  if (!loading && !error && qry.data?.graph) {
    try {
      data = qry?.data?.graph;
      if (!data) throw new Error(errorMsg as string);
      if (data?.Error) throw new Error(`${data.Error}`);

      let segmentStr = filters.segmentos[filters.segment] || "Mercado";
      if (segmentStr === "Total") segmentStr = "Mercado";

      const segmentPos = data.filter((d: any) => Object.keys(d)[0] === segmentStr);
      const cData: any = Object.values(data[data.indexOf(segmentPos[0])])[0];

      labels = Object.keys(cData.Ventas);
      values_total = [Object.values(cData.Ventas)];
      values_desglose = [Object.values(cData.Tickets), Object.values(cData.TicketMedio)];
    } catch (err: any) {
      error = true;
      errorMsg = err.message ?? (t("An-error-has-occur-when-recovering-data") as string);
    }
  }

  
  const exportData = useMemo(
    () => (data ? translateExportKeys(data, t) : data),
    
    [JSON.stringify(data), t]
  );

  let max: number | undefined = useMemo(
    () => getMax([...values_total, ...values_desglose]),
    [values_total, values_desglose]
  );
  let min: number | undefined = useMemo(
    () => getMin([...values_total, ...values_desglose]),
    [values_total, values_desglose]
  );

  const stepSize = useMemo(() => getSteps(min, max), [min, max]);

  max = Math.ceil((max ?? 0) + 1);
  min = Math.floor(min ?? 0);

  const values = isDesglosado ? values_desglose : values_total;

  
  const translatedLabels = labels.map((l) => translateOccasionLabel(l, t));

  const graphData = {
    datalabels: isDesglosado ? [t("Tickets"), t("Average-ticket")] : [t("Sales")],
    labels: translatedLabels,
    values: values,
    colors: isDesglosado ? ["#0C4D7B", "#85b709"] : ["#0e6773"],
    annotation: [],
    series: isDesglosado
      ? [
          { name: t("Tickets"), data: values[0] ?? [] },
          { name: t("Average-ticket"), data: values[1] ?? [] },
        ]
      : [{ name: t("Sales"), data: values[0] }],
    min: min,
    max: max,
    stepSize: stepSize,
  };

  return (
    <>
      <Title
        title={t("Occasion-position")}
        error={!!error}
        files={{
          data: exportData,
          semanas,
          user,
          title: t("Occasion-position") as string,
          leyenda: t("Moment") as string,
        }}
      />
      {error && <MessageBar isMultiline>{errorMsg}</MessageBar>}
      {loading && (
        <div className="loading-graph">
          <Spinner size={SpinnerSize.large} />
        </div>
      )}
      {!error && <BarGraph data={graphData} horizontal percentages />}
    </>
  );
};

export default Graph;